/**
 * K 域报表**接口契约** —— `GET /api/report`（四段全时间轴）
 *
 * 依据：K 域 PRD **§4.1**（四段）/ **§4.2**（K1）/ **§4.3**（K2）· **§4.5**（K4 七项 + 三层）·
 * `AC-K1.2`（`from`+`to` 同时存在才走区间）/ `AC-K4.1`（七项**服务端单接口下发**）·
 * `DR-013` / `DR-015` · 规格 **§12** · 架构 **§6.1**（契约唯一源）。
 *
 * ## 🔴 本文件**不含任何口径** —— 只描述"下发形状"
 *
 * | 段 | 数字来自 | 本文件 |
 * |---|---|---|
 * | ① 已发生（K1 趋势 / K2 分类占比 / 异常） | `nucleus/expense.ts`（唯一支出内核，**分组版**）+ `nucleus/trend.ts` + `nucleus/anomaly.ts` | 只定形状 |
 * | ② 现在（K4 七项 + 三层动作） | `nucleus/health.ts` + `nucleus/health-advice.ts` | 只定形状 |
 * | ③ 近期（预算执行） | 🔴 **不自建** —— 端侧复用 `GET /api/budgets` / `/api/spendable` / `/api/upcoming`（同源即逐分恒等，`AC-K3.5`） | — |
 * | ④ 远期 | 🔴 **P1**（P0 只给结构占位，`AC-K5.10`） | 标记 `phase: 'p1'` |
 *
 * ## 🔴 为什么 ② 段的净资产**不在本接口里**
 *
 * PRD §4.1 对第②段的原话是「净资产**复用 J 域同一组件、同一函数**」。端侧直接调
 * `GET /api/networth` 并渲染 `ui/NetWorthChart.tsx` / 净资产卡 —— 这**就是**复用；
 * 若本接口再把净资产搬一遍，反而会造出"报表页净资产"与"首页净资产"**两条下发路径**
 * （同 `DR-012` 对"双口径"的处置）。⇒ **本接口只下发 K 域自己的两块（① 与 ② 的健康线）+ ④ 占位。**
 *
 * ## 🔴 「未就绪」与「数据不足」必须能区分（诚实性）
 *
 * `HealthMetricView` 上有**两个**表示"没有数"的字段，语义不同：
 *
 * | 字段 | 含义 | UI |
 * |---|---|---|
 * | `sufficient: false` | 有取数来源，但**分母为 0**（如新账本无收入） | 「数据不足」 |
 * | `unavailable: {...}` | 🔴 **取数来源未就绪**（如投资模块未落地 ⇒ `holdingsValue` 恒 0） | 「未就绪」+ `missing` |
 *
 * 这是同一条纪律的第三次落地（前两次 = `NetWorthResponse.notReady` / `SpendableKind.noBudget`）：
 * **宁可显示"还没有"，也不让"接口没写"伪装成"这项是 0"**。若把 `unavailable` 当成 `0`，
 * 投资率会显示成 `0.00%` —— 而事实是"投资模块还没做"。
 */

import { z } from 'zod';
import type { Cents, RatioE4 } from '../types/brand.js';
import type { HealthMetricKey } from '../nucleus/health.js';
import type { HealthAdviceKind, HealthLayerKey } from '../nucleus/health-advice.js';
import { ReportPolicy } from '../constants/index.js';

// ───────────────────────────── 查询 ─────────────────────────────

/**
 * `GET /api/report` 的查询契约。
 *
 * 🔴 **`AC-K1.2` 的落点**：`from` + `to` **同时存在**才走自定义区间分桶；否则回落 `months`。
 * v1 的行为是"只传其一也回落"（静默），而**静默回落**会让调用方以为走了区间 ⇒
 * 本契约把规则写进 schema 注释 + 服务端只在两者齐备时采用区间，
 * 且**前端禁止只传其一**（`M1` 场景）。
 */
export const ReportQuerySchema = z.object({
  /** 趋势月数（缺省 = `ReportPolicy.DefaultTrendMonths`，上限 = `MaxTrendMonths`） */
  months: z.coerce.number().int().min(1).max(ReportPolicy.MaxTrendMonths).optional(),
  /** 自定义区间起点（`YYYY-MM-DD`）—— 必须与 `to` 同时出现 */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式须为 YYYY-MM-DD').optional(),
  /** 自定义区间终点（`YYYY-MM-DD`）—— 必须与 `from` 同时出现 */
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '日期格式须为 YYYY-MM-DD').optional(),
});
export type ReportQuery = z.infer<typeof ReportQuerySchema>;

// ─────────────────────────── ① 已发生（K1 + K2） ───────────────────────────

/** 报表所选窗口（服务端算好下发 —— 端侧不算日期） */
export interface ReportWindowView {
  /** `YYYY-MM-DD`（**账本本地日历日**） */
  from: string;
  /** `YYYY-MM-DD`（含） */
  to: string;
  /** 实际采用的月数（走自定义区间时 = 区间覆盖的整月数） */
  months: number;
  /** 是否走了自定义区间（`from` + `to` 齐备）—— 端侧据此决定是否显示"自定义"档 */
  custom: boolean;
}

/** 趋势的一个月桶 */
export interface TrendBucketView {
  /** `YYYY-MM` */
  month: string;
  expense: Cents;
  /** 该月**计入收支的收入**（`nucleus/income.ts` 唯一口径） */
  income: Cents;
}

/** 一笔离群项（`AC-K2.15`/`AC-K2.16`：只报"不太寻常"，不评分不排名） */
export interface ReportAnomalyView {
  /** 腿 id（下钻据此定位） */
  legId: string;
  amount: Cents;
  /** `amount − μ` */
  deviation: Cents;
  /** 偏离倍数（`E4`）；`null` = `σ` 取整后为 0（跳过该判定） */
  sigmaE4: RatioE4 | null;
}

/**
 * 分类占比树的一个节点（**L1 与 L2 同形**，按 `level` 区分 —— 与 `CategoryView` 同一手法）。
 *
 * 🔴 `isUncategorized` = `true` 的节点（哨兵，`categoryId = __uncategorized__`）
 * **永远没有 `children`**（`AC-K2.2`：**绝不能当父节点**），也**不参与 L1 排序**
 * （`AC-K2.14`：0 元占位行排末尾）。
 */
export interface CategoryNodeView {
  categoryId: string;
  name: string;
  level: 1 | 2;
  /** 哨兵（未分类）—— 见上 */
  isUncategorized: boolean;
  /** 净额（= 该分类 `baseExpense + amortized − reduction`，**恒 ≥ 0**） */
  amount: Cents;
  /** 占本窗口总支出的比例（`E4`）—— 🔴 Σ 各 L1 的 `amount` ≡ `totalExpense`（`AC-K2.4`） */
  shareE4: RatioE4;
  children: readonly CategoryNodeView[];
  /**
   * 🆕 批 `112-B`：`true` = 该分类**已被用户移除**（`DR-062` D3 软删），
   * 但历史支出**仍归属它**（不是"未分类"）—— `AC-B3.13` 在报表侧的兑现。
   *
   * 🔴 **可选键、只在为 `true` 时出现**（铁律 63）。端侧据此加「已移除」标注，
   * 避免用户困惑：分类明明移除了，报表里为什么还在。
   */
  removed?: true;
}

/** ① 段「钱花到哪儿去了」 */
export interface ReportSpentView {
  currency: string;
  /** 窗口总支出（= Σ 分类净额 = 唯一支出内核的 `total`） */
  totalExpense: Cents;
  /** 窗口收入合计 */
  totalIncome: Cents;
  /** 窗口自然日天数（含当天） */
  windowDays: number;
  /** 🔴 **窗口内日均**（≠ 近 3 月月均，`AC-K1.8`） */
  dailyAverage: Cents;
  /** 按月分桶的趋势 */
  trend: readonly TrendBucketView[];
  /** 同比（对比**去年同期**）；`null` = 无基期（`AC-K1.5`：显示「—」，**不显示 ∞**） */
  yoyE4: RatioE4 | null;
  /** 环比（对比**上一期**）；`null` = 无基期 */
  momE4: RatioE4 | null;
  /** 两级分类树（L1 升序按金额降序；哨兵与 0 元占位排末尾） */
  categories: readonly CategoryNodeView[];
  /** 未分类支出（= 哨兵节点的 `amount`，便于调用方直接取用） */
  uncategorized: Cents;
  /** σ 离群项（空数组 = 无异常，**不是"没检测"**） */
  anomalies: readonly ReportAnomalyView[];
  /** 有腿因缺该日汇率未折算 ⇒ 本币合计**不完整**（`AC-K2.13`：不静默、不按 1:1 冒充） */
  fxIncomplete: boolean;
}

// ─────────────────────────── ② 现在（K4 七项） ───────────────────────────

/** 「这项的取数来源还没就绪」—— 🔴 **不是 0** */
export interface HealthUnavailableView {
  /** 面向用户的原因（可直接展示） */
  missing: string;
  /** 已建好的表名；`null` = 连表都没有 */
  table: string | null;
}

export interface HealthMetricView {
  key: HealthMetricKey;
  /** 展示名（服务端下发 —— 端侧不重写，`health.ts` 的 `LABELS` 唯一源） */
  label: string;
  /** 百分比类 = `E4`；应急月数 = 「月 × E4」。🔴 不可评估时为 `null` */
  valueE4: RatioE4 | null;
  /** `false` ⇒ 分母为 0（UI 显示「数据不足」） */
  sufficient: boolean;
  /** 非 `null` ⇒ **来源未就绪**（UI 显示「未就绪」，优先级高于「数据不足」） */
  unavailable: HealthUnavailableView | null;
}

/** 一个可核对的动作（`AC-K4.15`）—— 文案模板在端侧唯一源，本处只给 `kind` + 数字 */
export interface HealthAdviceView {
  kind: HealthAdviceKind;
  /** 主数字（`emergency-topup` = 每月多存；`trim-premium` = 每年可释放）；`cut-expense` 为 `null` */
  amount: Cents | null;
  /** 达成月数（仅 `emergency-topup`） */
  months: number | null;
  /** 主指标当前值（`E4`） */
  currentE4: RatioE4 | null;
  /** 目标值（`E4`） */
  targetE4: RatioE4 | null;
}

/** 三层里的一层 */
export interface HealthLineView {
  layer: HealthLayerKey;
  /** 该层指标（顺序 = `HEALTH_LINES` 的定义顺序） */
  metrics: readonly HealthMetricView[];
  /** 🔴 该层**动作**；`null` = 无数字支撑 ⇒ **只出陈述、不出动作**（`AC-K4.16`） */
  advice: HealthAdviceView | null;
}

/**
 * ② 段「家底什么样」的**健康线部分**。
 *
 * 🔴 **只含至少一项可评估的层**（`AC-K4.17`：整层数据不足 ⇒ 整层不显示，
 * 由 `isLineVisible` 判定）—— 故 `lines` 可能少于 3 层，端侧**不得**补空层。
 */
export interface ReportHealthView {
  lines: readonly HealthLineView[];
}

// ─────────────────────────── ④ 远期（P1 占位） ───────────────────────────

/**
 * ④ 段状态 —— 🔴 **P1**（`AC-K5.10`：P0 阶段只给结构占位 + 说明文案，**不留空白区**）。
 *
 * 文案在**端侧**唯一源（`features/report-labels.ts`），本处只标记相位，
 * 让端侧能区分"这段是刻意留的"与"这段忘了做"。
 */
export interface ReportFutureView {
  phase: 'p1';
}

// ─────────────────────────── 响应 ───────────────────────────

export interface ReportResponse {
  window: ReportWindowView;
  /** ① 已发生 */
  spent: ReportSpentView;
  /** ② 现在的健康线（净资产另走 `GET /api/networth`，见文件头） */
  health: ReportHealthView;
  /** ④ 远期（P1 占位） */
  future: ReportFutureView;
  /**
   * 🆕 **本窗口内按「简化记账」记的工资笔数**（批 141 · `DR-016` §3.6 / C 域 §5.2.1⑤）。
   *
   * > 0 ⇒ 这些工资的收入按**到手（实发）**记，**未含社保与个税** —— 与完整工资条
   * （收入按税前）口径不同，税负与储蓄率的**分母不完整**。
   *
   * 🔴 为什么由服务端数：端侧要数就得按窗口 + 标记再筛一遍流水 ⇒ 第二套窗口口径
   * （服务端窗口含账本时区，端侧算不出来）。
   */
  simplifiedSalaryCount: number;
}
